import React, {Component, createRef} from "react"
import { Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import DOMPurify from 'dompurify'
import {store} from "../../App";
import { setDialog, clearDialog } from "../../Redux/OverlayActions";
import { withTranslation } from "../../hooks/withTranslation";

class TruckMarker extends Component {
    constructor() {
        super();
        this.markerRef = createRef();
        this.animationFrame = null;
        this.statusToText = this.statusToText.bind(this);
        this.openRouteWithDisclaimer = this.openRouteWithDisclaimer.bind(this);
    }

    componentDidMount() {
    }

    // Prevent unnecessary re-renders during polling - only re-render if relevant props changed
    shouldComponentUpdate(nextProps) {
        const kar = this.props.kar;
        const nextKar = nextProps.kar;

        // Re-render if position changed (for animation)
        if (this.props.lat !== nextProps.lat || this.props.lng !== nextProps.lng) {
            return true;
        }
        // Re-render if availability or status changed
        if (kar.available !== nextKar.available || kar.status !== nextKar.status) {
            return true;
        }
        // Re-render if trackid changed (for tooltip state)
        if (this.props.trackid !== nextProps.trackid) {
            return true;
        }
        return false;
    }

    componentDidUpdate(prevProps) {
        // Animate marker when position changes
        if (prevProps.lat !== this.props.lat || prevProps.lng !== this.props.lng) {
            this.animateMarker(
                [prevProps.lat, prevProps.lng],
                [this.props.lat, this.props.lng]
            );
        }
    }

    componentWillUnmount() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
    }

    animateMarker(fromLatLng, toLatLng) {
        const marker = this.markerRef.current;
        if (!marker) return;

        const duration = 1000; // 1 second animation
        const startTime = performance.now();
        const startLat = fromLatLng[0];
        const startLng = fromLatLng[1];
        const deltaLat = toLatLng[0] - startLat;
        const deltaLng = toLatLng[1] - startLng;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease-out cubic for smooth deceleration
            const easeProgress = 1 - Math.pow(1 - progress, 3);

            const newLat = startLat + (deltaLat * easeProgress);
            const newLng = startLng + (deltaLng * easeProgress);

            marker.setLatLng([newLat, newLng]);

            if (progress < 1) {
                this.animationFrame = requestAnimationFrame(animate);
            }
        };

        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
        this.animationFrame = requestAnimationFrame(animate);
    }

    openRouteWithDisclaimer(lat, lng) {
        const kar = this.props.kar;
        const { t } = this.props;
        const messageTemplate = t('tracker.truckMarker.routeDisclaimer.message', '<strong>Let op:</strong> IJskar {title} kan zich intussen verplaatsen. Check regelmatig de app om te zien waar de ijskar zich bevindt.');
        store.dispatch(setDialog({
            title: t('tracker.truckMarker.routeDisclaimer.title', 'Route plannen'),
            message: messageTemplate.replace('{title}', kar.title),
            theme: "warning",
            btn1Text: t('tracker.truckMarker.routeDisclaimer.openButton', 'Open routeplanner'),
            btn1Theme: "primary",
            btn1Title: "open",
            btn2Text: t('common.cancel', 'Annuleer'),
            btn2Title: "cancel",
            response: (response) => {
                store.dispatch(clearDialog());
                if (response === "open") {
                    // Open Google Maps with directions
                    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                    window.open(mapsUrl, '_blank');
                }
            },
        }));
    }

    statusToText(kar) {
        const { t } = this.props;
        const zoneName = kar.status_payload?.scheduled_at_name;

        switch (kar.status) {
            case "STARTUP":
                return zoneName
                    ? t('tracker.truckMarker.status.startupWithZone', 'Vertrekt zodadelijk richting {zone}').replace('{zone}', zoneName)
                    : t('tracker.truckMarker.status.startup', 'Vertrekt zodadelijk');
            case "EN_ROUTE_TO_ZONE":
                return zoneName
                    ? t('tracker.truckMarker.status.enRouteToZoneWithName', 'Onderweg naar {zone}').replace('{zone}', zoneName)
                    : t('tracker.truckMarker.status.enRouteToZone', 'Onderweg naar zone');
            case "IN_ZONE":
                return zoneName
                    ? t('tracker.truckMarker.status.inZoneWithName', 'Aan het rijden in {zone}').replace('{zone}', zoneName)
                    : t('tracker.truckMarker.status.inZone', 'Aan het rijden');
            case "EN_ROUTE_TO_DEPOT":
                return t('tracker.truckMarker.status.soldOut', 'Uitverkocht');
            case "CLEANUP":
                return t('tracker.truckMarker.status.soldOut', 'Uitverkocht');
            case "FINISHED":
                return t('tracker.truckMarker.status.soldOut', 'Uitverkocht');
            case "SCHEDULED":
                return zoneName
                    ? t('tracker.truckMarker.status.scheduledWithZone', 'Gepland in {zone}').replace('{zone}', zoneName)
                    : t('tracker.truckMarker.status.scheduled', 'Gepland');
            case "NOT_SCHEDULED":
                return t('tracker.truckMarker.status.notScheduled', 'Niet gepland vandaag');
            case "EN_ROUTE_TO_ICECREAM_VAN_RESERVATION":
                return t('tracker.truckMarker.status.enRouteToReservation', 'Onderweg naar reservatie');
            case "AT_ICECREAM_VAN_RESERVATION":
                return t('tracker.truckMarker.status.atReservation', 'Op reservatie');
            case "ON_BREAK":
                return t('tracker.truckMarker.status.onBreak', 'In pauze');
            default:
                return '';
        }
    }

    render() {
        const kar = this.props.kar;
        const { t } = this.props;

        // Use DivIcon to combine background circle and number text in one element
        const bgImage = kar.available ? "/images/tracker/icon_kar_available.png" : "/images/tracker/icon_kar_notavailable.png";
        const truckNumber = (kar.title && kar.title.match(/^#(\d+)/)) ? kar.title.match(/^#(\d+)/)[1] : kar.id;

        const myIcon = L.divIcon({
            className: 'truck-marker-icon',
            html: `<div class="truck-marker-container">
                <img src="${bgImage}" class="truck-marker-bg" />
                <span class="truck-marker-number">${truckNumber}</span>
            </div>`,
            iconSize: [40, 40],
            iconAnchor: [20, 20],
            popupAnchor: [0, -20],
        });

        return (
            <Marker
                ref={this.markerRef}
                icon={myIcon}
                key={"karmarker"+this.props.kar.id}
                zIndexOffset={1000}
                eventHandlers={{
                    click: () => {this.props.onClick(this.props.trackid, kar.latitude, kar.longitude);}
                }}
                position={[this.props.lat, this.props.lng]}
                >
                <Popup className="truck-popup">
                    <div className="truck-popup-content">
                        <h6 className="truck-popup-title">{t('tracker.truckMarker.popup.title', 'IJskar {title}').replace('{title}', kar.title)}</h6>
                        {kar.subtitle ? <p className="truck-popup-subtitle" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(kar.subtitle, { ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'br'] })}}></p> : null}
                        <div className="truck-popup-status">
                            <span className={`status-badge ${kar.available ? 'available' : 'unavailable'}`}>
                                {kar.available ? t('tracker.truckMarker.status.available', 'Beschikbaar') : t('tracker.truckMarker.status.unavailable', 'Niet beschikbaar')}
                            </span>
                            <span className="status-text">{this.statusToText(kar)}</span>
                        </div>
                        <button
                            type="button"
                            className="truck-popup-route-btn"
                            onClick={() => this.openRouteWithDisclaimer(kar.latitude, kar.longitude)}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                <circle cx="12" cy="10" r="3"/>
                            </svg>
                            {t('tracker.truckMarker.popup.routeButton', 'Route plannen')}
                        </button>
                    </div>
                </Popup>
            </Marker>
        )
    }
}

export default withTranslation(TruckMarker)
